import React, { useState, useEffect } from 'react';
import { routeAPI, stationAPI, cityAPI, fareAPI } from '../services/api';
import { RouteRequest, RouteResponse, Station, City, FareCalculationRequest, FareCalculationResponse } from '../types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Autocomplete
} from '@mui/material';
import {
  Search,
  Train,
  AccessTime,
  SwapHoriz,
  Refresh,
  Route,
  LocationOn,
  TrendingUp,
  LocalAtm
} from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

// Helper functions
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const formatDistance = (meters: number) => {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${meters}m`;
};

const RoutePlanning: React.FC = () => {
  const [route, setRoute] = useState<RouteResponse | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [fromStation, setFromStation] = useState<Station | null>(null);
  const [toStation, setToStation] = useState<Station | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStations, setLoadingStations] = useState(false);
  const [loadingFare, setLoadingFare] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fareResult, setFareResult] = useState<FareCalculationResponse | null>(null);
  const [criteria, setCriteria] = useState<'SHORTEST_DISTANCE' | 'SHORTEST_TIME' | 'LEAST_INTERCHANGES' | 'BALANCED'>('SHORTEST_TIME');

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCityId) {
      fetchStationsByCity(selectedCityId);
    } else {
      setStations([]);
      setFromStation(null);
      setToStation(null);
    }
  }, [selectedCityId]);

  const fetchCities = async () => {
    try {
      const res = await cityAPI.getAll();
      setCities(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load cities:', error);
    }
  };

  const fetchStationsByCity = async (cityId: string) => {
    setLoadingStations(true);
    try {
      const res = await stationAPI.getByCity(cityId);
      setStations(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load stations:', error);
      setError('Failed to load stations for selected city');
    } finally {
      setLoadingStations(false);
    }
  };

  const handleCityChange = (event: SelectChangeEvent<string>) => {
    const cityId = event.target.value;
    setSelectedCityId(cityId);
    const city = cities.find(c => c.id === cityId);
    setSelectedCity(city || null);
    setRoute(null);
    setFromStation(null);
    setToStation(null);
    setError(null);
  };

  const findRoute = async () => {
    if (!fromStation || !toStation) {
      setError('Please select both from and to stations');
      return;
    }

    setLoading(true);
    setError(null);
    setRoute(null);

    try {
      const response = await routeAPI.quickRoute(
        fromStation.stationCode,
        toStation.stationCode,
        criteria
      );
      setRoute(response.data);
      setFareResult(null); // Clear previous fare result when route changes
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to find route');
    } finally {
      setLoading(false);
    }
  };

  const handleSwapStations = () => {
    const temp = fromStation;
    setFromStation(toStation);
    setToStation(temp);
  };

  const calculateFare = async () => {
    if (!route || !selectedCity) {
      setError('Please find a route first');
      return;
    }

    setLoadingFare(true);
    setError(null);
    setFareResult(null);

    try {
      // Build lines array from route segments
      // Each segment represents a line with its stations
      const lines: Array<{ lineId: string; stations: string[] }> = [];
      
      route.segments.forEach(segment => {
        // Skip walk segments
        if (segment.lineCode === 'WALK' || segment.lineCode === 'walk') {
          return;
        }

        // Collect all station IDs for this line segment
        const stations: string[] = [];
        
        // Add from station
        if (segment.fromStationId) {
          stations.push(segment.fromStationId);
        }
        
        // Add intermediate stations
        if (segment.intermediateStationIds && segment.intermediateStationIds.length > 0) {
          segment.intermediateStationIds.forEach(id => {
            if (id && !stations.includes(id)) {
              stations.push(id);
            }
          });
        }
        
        // Add to station
        if (segment.toStationId && !stations.includes(segment.toStationId)) {
          stations.push(segment.toStationId);
        }

        // Only add line segment if it has a lineId and stations
        if (segment.lineId && stations.length > 0) {
          lines.push({
            lineId: segment.lineId,
            stations: stations
          });
        }
      });

      // Get current date in ISO format (YYYY-MM-DD)
      const now = new Date();
      const journeyDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      // Get journey start time in ISO format
      const journeyStartTime = now.toISOString();

      // Prepare fare calculation request with new structure
      const fareRequest: FareCalculationRequest = {
        cityId: selectedCity.id || selectedCityId,
        distanceInMeters: route.totalDistance,
        travelTimeInMinutes: route.totalDuration,
        lines: lines,
        journeyDate: journeyDate,
        journeyStartTime: journeyStartTime,
        additionalMetaData: {
          routeId: route.routeId,
          numberOfInterchanges: route.numberOfInterchanges || 0,
          directRoute: route.isDirectRoute || false,
        }
      };

      const response = await fareAPI.calculateFare(fareRequest);
      setFareResult(response.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to calculate fare');
    } finally {
      setLoadingFare(false);
    }
  };

  const parseColor = (colorString?: string): string => {
    if (!colorString) return '#1976d2';
    // Handle hex colors
    if (colorString.startsWith('#')) {
      return colorString;
    }
    // Handle color names - convert to hex if needed
    const colorMap: Record<string, string> = {
      'red': '#f44336',
      'blue': '#2196f3',
      'green': '#4caf50',
      'yellow': '#ffeb3b',
      'orange': '#ff9800',
      'purple': '#9c27b0',
      'pink': '#e91e63',
      'teal': '#009688',
      'cyan': '#00bcd4',
      'brown': '#795548',
      'grey': '#9e9e9e',
      'black': '#000000',
    };
    return colorMap[colorString.toLowerCase()] || '#1976d2';
  };

  const hexToRgba = (hex: string, alpha: number): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(25, 118, 210, ${alpha})`;
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Route Planning
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => {
          setRoute(null);
          setError(null);
        }}>
          Clear Results
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Route Planning Form */}
      <Paper sx={{ mx: 3, mb: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Plan Your Route
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select
                value={selectedCityId}
                onChange={handleCityChange}
                label="City"
                disabled={loadingStations}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3.5}>
            <Autocomplete
              options={stations}
              getOptionLabel={(option) => `${option.name} (${option.stationCode})`}
              value={fromStation}
              onChange={(_, newValue) => setFromStation(newValue)}
              disabled={!selectedCityId || loadingStations}
              loading={loadingStations}
              renderInput={(params) => (
                <TextField {...params} label="From Station" />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.stationCode}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <IconButton
                onClick={handleSwapStations}
                disabled={!fromStation || !toStation}
                color="primary"
              >
                <SwapHoriz />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={3.5}>
            <Autocomplete
              options={stations}
              getOptionLabel={(option) => `${option.name} (${option.stationCode})`}
              value={toStation}
              onChange={(_, newValue) => setToStation(newValue)}
              disabled={!selectedCityId || loadingStations}
              loading={loadingStations}
              renderInput={(params) => (
                <TextField {...params} label="To Station" />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.stationCode}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Optimization Criteria</InputLabel>
              <Select
                value={criteria}
                onChange={(e) => setCriteria(e.target.value as any)}
                label="Optimization Criteria"
              >
                <MenuItem value="SHORTEST_TIME">Shortest Time</MenuItem>
                <MenuItem value="SHORTEST_DISTANCE">Shortest Distance</MenuItem>
                <MenuItem value="LEAST_INTERCHANGES">Least Interchanges</MenuItem>
                <MenuItem value="BALANCED">Balanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={findRoute}
                disabled={loading || !fromStation || !toStation}
                sx={{ minWidth: 150 }}
              >
                {loading ? <CircularProgress size={20} /> : 'Find Route'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Route Results */}
      {route && (
        <Paper sx={{ mx: 3, mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Route Details
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={loadingFare ? <CircularProgress size={20} /> : <LocalAtm />}
              onClick={calculateFare}
              disabled={loadingFare || !selectedCity}
            >
              {loadingFare ? 'Calculating...' : 'Calculate Fare'}
            </Button>
          </Box>
          <RouteResultsDisplay route={route} parseColor={parseColor} hexToRgba={hexToRgba} />
        </Paper>
      )}

      {/* Fare Result */}
      {fareResult && (
        <Paper sx={{ mx: 3, mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalAtm sx={{ fontSize: 24, color: 'success.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Fare Calculation Result
            </Typography>
          </Box>
          
          {/* Standard Fare (Default) */}
          <Card
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              boxShadow: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                  Standard Fare
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {fareResult.currency} {fareResult.fareAmount.toFixed(2)}
                </Typography>
              </Box>
              {fareResult.appliedRuleName && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                    Applied Rule
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fareResult.appliedRuleName}
                  </Typography>
                  {fareResult.appliedRuleType && (
                    <Chip
                      label={fareResult.appliedRuleType}
                      size="small"
                      sx={{ mt: 0.5 }}
                      color="success"
                    />
                  )}
                </Box>
              )}
            </Box>
          </Card>

          {/* Fares by Category */}
          {fareResult.faresByCategory && Object.keys(fareResult.faresByCategory).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Fares by Category
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(fareResult.faresByCategory).map(([category, fareDetails]) => (
                  <Grid item xs={12} sm={6} md={4} key={category}>
                    <Card
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: category === (fareResult.appliedFareCategory || 'STANDARD') ? '2px solid' : '1px solid',
                        borderColor: category === (fareResult.appliedFareCategory || 'STANDARD') ? 'success.main' : 'divider',
                        backgroundColor: category === (fareResult.appliedFareCategory || 'STANDARD') ? 'success.light' : 'background.paper',
                        boxShadow: 1
                      }}
                    >
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {category.replace(/_/g, ' ')}
                          </Typography>
                          {category === (fareResult.appliedFareCategory || 'STANDARD') && (
                            <Chip label="Applied" size="small" color="success" />
                          )}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: category === (fareResult.appliedFareCategory || 'STANDARD') ? 'success.contrastText' : 'text.primary' }}>
                          {fareDetails.currency} {fareDetails.fareAmount.toFixed(2)}
                        </Typography>
                        {fareDetails.appliedRuleName && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                            {fareDetails.appliedRuleName}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Matching Rules */}
          {fareResult.matchingRules && fareResult.matchingRules.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                All Matching Rules ({fareResult.matchingRules.length})
              </Typography>
              <List dense>
                {fareResult.matchingRules.map((rule, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      bgcolor: rule.ruleId === fareResult.appliedRuleId ? 'action.selected' : 'background.paper',
                      borderRadius: 1,
                      mb: 0.5
                    }}
                  >
                    <ListItemIcon>
                      <LocalAtm />
                    </ListItemIcon>
                    <ListItemText
                      primary={rule.ruleName}
                      secondary={
                        <Box>
                          <Typography variant="caption" component="span">
                            {fareResult.currency} {rule.fareAmount.toFixed(2)} • Priority: {rule.priority} • Type: {rule.ruleType}
                            {rule.fareCategory && ` • Category: ${rule.fareCategory.replace(/_/g, ' ')}`}
                          </Typography>
                          {rule.reason && (
                            <Typography variant="caption" component="div" sx={{ display: 'block', mt: 0.5 }}>
                              {rule.reason}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

// Route Results Display Component (similar to Flutter lookup view)
const RouteResultsDisplay: React.FC<{ route: RouteResponse; parseColor: (color?: string) => string; hexToRgba: (hex: string, alpha: number) => string }> = ({ route, parseColor, hexToRgba }) => {
  const isDirectRoute = route.isDirectRoute || (route.linesUsed && route.linesUsed.length === 1);
  const routeType = isDirectRoute ? 'Direct Route' : 'Multi Route';

  return (
          <Box>
      {/* Route Summary Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box
            sx={{
              padding: 1,
              borderRadius: 1,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Route sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="h6">Route Path</Typography>
        </Box>

        {/* Route Summary Card */}
        <Card
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {isDirectRoute ? (
                <TrendingUp sx={{ fontSize: 16, color: 'text.secondary' }} />
              ) : (
                <SwapHoriz sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                {routeType} • {formatDistance(route.totalDistance)}
            </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
                {route.totalDuration}min
            </Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Source Station */}
      {route.sourceStationName && (
        <Box sx={{ mb: 2 }}>
          <SourceDestinationHeader
            text={`From: ${route.sourceStationName}`}
            isSource={true}
            lineColor={route.segments[0] ? parseColor(route.segments[0].lineColor) : '#1976d2'}
            lineName={route.segments[0]?.lineName || route.segments[0]?.lineCode || ''}
            hexToRgba={hexToRgba}
          />
        </Box>
      )}

      {/* Route Segments */}
      <Box sx={{ mb: 2 }}>
        {route.segments.map((segment, index) => {
          const segmentColor = parseColor(segment.lineColor);
          const isLastSegment = index === route.segments.length - 1;

          return (
            <Box key={index}>
              {/* Intermediate Stations */}
              {segment.intermediateStations && segment.intermediateStations.length > 0 && (
                <Box>
                  {segment.intermediateStations.map((station, stationIndex) => (
                    <StationItem
                      key={stationIndex}
                      stationName={station.stationName}
                      stationCode={station.stationCode}
                      lineColor={segmentColor}
                      isStart={false}
                      isEnd={false}
                      hexToRgba={hexToRgba}
                    />
                  ))}
                </Box>
              )}

              {/* Line Change Indicator (if not last segment) */}
              {!isLastSegment && route.segments[index + 1] && (
                <LineChangeIndicator
                  interchangeStation={segment.toStationName || 'Station'}
                  currentLineName={segment.lineName || segment.lineCode || 'Line'}
                  currentLineColor={segmentColor}
                  nextLineName={route.segments[index + 1].lineName || route.segments[index + 1].lineCode || 'Line'}
                  nextLineColor={parseColor(route.segments[index + 1].lineColor)}
                  hexToRgba={hexToRgba}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Destination Station */}
      {route.destinationStationName && (
        <Box>
          <SourceDestinationHeader
            text={`To: ${route.destinationStationName}`}
            isSource={false}
            lineColor={route.segments[route.segments.length - 1] ? parseColor(route.segments[route.segments.length - 1].lineColor) : '#1976d2'}
            hexToRgba={hexToRgba}
          />
        </Box>
      )}
    </Box>
  );
};

// Source/Destination Header Component
const SourceDestinationHeader: React.FC<{
  text: string;
  isSource: boolean;
  lineColor: string;
  lineName?: string;
  hexToRgba: (hex: string, alpha: number) => string;
}> = ({ text, isSource, lineColor, lineName, hexToRgba }) => {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: hexToRgba(lineColor, 0.12),
        border: '1px solid',
        borderColor: hexToRgba(lineColor, 0.5)
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `2.5px solid ${lineColor}`,
            backgroundColor: isSource ? lineColor : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 2px 4px ${hexToRgba(lineColor, 0.25)}`
          }}
        >
          {!isSource && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: lineColor
              }}
            />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {text}
          </Typography>
          {lineName && (
            <Typography variant="caption" color="text.secondary">
              {lineName}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 24,
            height: 4,
            borderRadius: 0.5,
            backgroundColor: lineColor,
            boxShadow: `0 1px 2px ${hexToRgba(lineColor, 0.25)}`
          }}
        />
      </Box>
    </Box>
  );
};

// Station Item Component
const StationItem: React.FC<{
  stationName: string;
  stationCode: string;
  lineColor: string;
  isStart: boolean;
  isEnd: boolean;
  hexToRgba: (hex: string, alpha: number) => string;
}> = ({ stationName, lineColor, isStart, isEnd, hexToRgba }) => {
  return (
    <Box
      sx={{
        py: 1,
        px: 1,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `2px solid ${lineColor}`,
            backgroundColor: isStart || isEnd ? lineColor : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isStart || isEnd ? `0 2px 4px ${hexToRgba(lineColor, 0.25)}` : 'none'
          }}
        >
          {isEnd && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'background.paper'
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontWeight: isStart || isEnd ? 700 : 600
          }}
        >
          {stationName}
        </Typography>
        <Box
          sx={{
            width: 24,
            height: 4,
            borderRadius: 0.5,
            backgroundColor: lineColor,
            boxShadow: `0 1px 2px ${hexToRgba(lineColor, 0.25)}`
          }}
        />
      </Box>
    </Box>
  );
};

// Line Change Indicator Component
const LineChangeIndicator: React.FC<{
  interchangeStation: string;
  currentLineName: string;
  currentLineColor: string;
  nextLineName: string;
  nextLineColor: string;
  hexToRgba: (hex: string, alpha: number) => string;
}> = ({ interchangeStation, currentLineName, currentLineColor, nextLineName, nextLineColor, hexToRgba }) => {
  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              backgroundColor: 'warning.main',
              color: 'warning.contrastText',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <SwapHoriz sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Interchange at {interchangeStation}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
              <Box
                sx={{
                  width: 20,
                  height: 4,
                  borderRadius: 0.5,
                  backgroundColor: currentLineColor
                }}
              />
              <Typography variant="caption" color="text.secondary">
                →
              </Typography>
                <Box
                  sx={{
                  width: 20,
                  height: 4,
                  borderRadius: 0.5,
                  backgroundColor: nextLineColor
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                To {nextLineName}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.25,
                backgroundColor: hexToRgba(currentLineColor, 0.15),
                border: `1px solid ${hexToRgba(currentLineColor, 0.6)}`
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: currentLineColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 7, fontWeight: 700, color: 'white' }}>
                  1
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: currentLineColor }}>
                Exit Current Line
                </Typography>
                <Typography variant="caption" color="text.secondary">
                Get off train at {interchangeStation}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <Typography variant="body2" color="text.secondary">
            ↓
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.25,
                backgroundColor: hexToRgba(nextLineColor, 0.15),
                border: `1px solid ${hexToRgba(nextLineColor, 0.6)}`
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: nextLineColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 7, fontWeight: 700, color: 'white' }}>
                  2
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: nextLineColor }}>
                Board Next Line
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Board {nextLineName} at {interchangeStation}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RoutePlanning; 